import React, { useRef, useState } from 'react';
import { ArrowUpRight, Info } from 'lucide-react';
import { MonthlyRecommendation, PricingMonthlyRow } from './api';
import { monthlyChargeLabel, monthlyChargeReason, monthlyRowEvidence, monthlyRowLabel } from './monthlyPresentation';
import './monthly-price-info.css';

interface Props {
  row: PricingMonthlyRow;
  recommendation?: Pick<MonthlyRecommendation, 'activities' | 'benchmarks'> | null;
  onOpenReference?: (id: string) => void;
  label?: string;
}

/** Read-only explanation. It never analyzes, saves, changes prices or grants edit access. */
export default function MonthlyPriceInfo({ row, recommendation, onOpenReference, label }: Props) {
  const [open, setOpen] = useState(false);
  const disclosure = useRef<HTMLDetailsElement>(null);
  const trigger = useRef<HTMLElement>(null);
  const name = label || monthlyRowLabel(row) || 'service';
  const charges = row.simple?.charges ?? [];
  const evidence = monthlyRowEvidence(row, recommendation?.activities);
  const sourceNames = Array.from(new Set(evidence.map(source => source.source)));
  const fullLabels = Array.from(new Set(charges.filter(charge => monthlyChargeLabel(charge) !== charge.label).map(charge => charge.label)));
  const reasons = Array.from(new Map(charges.map(charge => [`${charge.ruleId}:${monthlyChargeLabel(charge)}`, charge])).values());
  const references = recommendation?.benchmarks ?? [];

  return <details ref={disclosure} className="monthly-price-info" open={open} onToggle={event => setOpen(event.currentTarget.open)}
    onKeyDown={event => {
      if (event.key !== 'Escape' || !open) return;
      event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current?.focus();
    }}>
    <summary ref={trigger} role="button" aria-expanded={open} aria-label={`Why this price for ${name}`} title="Why this price">
      <Info size={16} aria-hidden="true" />
    </summary>
    <div className="monthly-price-info-content">
      {charges.length ? <ul className="monthly-price-info-reasons">{reasons.map(charge => <li key={charge.id}>
        <strong>{monthlyChargeLabel(charge)}</strong><span>{monthlyChargeReason(charge)}</span>
      </li>)}</ul> : <p>Saved service terms.</p>}
      {sourceNames.length ? <div className="monthly-price-info-source-names"><span>Source</span>{sourceNames.slice(0, 2).map(source => <span key={source} title={source}>{source}</span>)}{sourceNames.length > 2 ? <span>+{sourceNames.length - 2} more</span> : null}</div> : null}
      {evidence.length || fullLabels.length ? <details className="monthly-price-info-sources"><summary>Source details</summary>
        {fullLabels.length ? <div className="monthly-price-info-activities"><strong>Activity</strong>{fullLabels.map(label => <p key={label}>{label}</p>)}</div> : null}
        <ul>{evidence.map((source, index) => <li key={index}><strong>{source.source}</strong><blockquote>{source.quote}</blockquote></li>)}</ul>
      </details> : null}
      {references.length ? <div className="monthly-price-info-references">
        <span>References for this recommendation</span>
        <ul>{references.map(reference => <li key={reference.id}>{onOpenReference
          ? <button type="button" title={reference.filename} onClick={() => onOpenReference(reference.id)}><span>{reference.client || reference.filename}</span><ArrowUpRight size={14} aria-hidden="true" /></button>
          : <span title={reference.filename}>{reference.client || reference.filename}</span>}</li>)}</ul>
      </div> : null}
    </div>
  </details>;
}
